import { Outlet, useMatches } from "react-router";

import { useNavigation } from "@/providers";
import {
  PageLayout,
  RobotoStyledTab,
  RobotoStyledTabs,
} from "@/shared/components";
import { type ActionsTabName } from "@/shared/navigation/types";

import styles from "./ActionsTabs.module.css";

/**
 * Route `handle` each tab route attaches so the tab bar can identify the
 * active tab from the matched route rather than by re-parsing the URL. See
 * `ActionsRoutes`.
 */
export interface ActionsTabHandle {
  actionsTab: ActionsTabName;
}

function actionsTabFromHandle(handle: unknown): ActionsTabName | undefined {
  return handle !== null && typeof handle === "object" && "actionsTab" in handle
    ? (handle as ActionsTabHandle).actionsTab
    : undefined;
}

/**
 * Tabs of the Actions page, in display order. Each tab has its own route, so
 * every tab is deep-linkable. Invocations lives at the top level
 * (`/invocations`) to share a prefix with its detail pages
 * (`/invocations/:id`); the rest sit under `/actions`. Triggers has its own
 * top-level section and no longer appears here.
 *
 * `tab` is the name understood by `Goto.actions` (navigation) and matched
 * against each route's `actionsTab` handle (active-tab detection).
 */
const ACTIONS_TABS = [
  { label: "Actions", title: "Actions", tab: "actions" },
  { label: "Hub", title: "Action Hub", tab: "hub" },
  { label: "Invocations", title: "Invocations", tab: "invocations" },
  { label: "Images", title: "Action Images", tab: "images" },
] as const satisfies ReadonlyArray<{
  label: string;
  title: string;
  tab: ActionsTabName;
}>;

/**
 * Layout for the Actions page: renders the page chrome and tab bar, with the
 * active tab's content supplied through the router `<Outlet />`.
 *
 * The page title follows the active tab, as `AgentsTabs` does, so the header
 * and browser tab name what the user is looking at. The tab body fills the
 * rest of the page, so a tab's `DataTable` scrolls inside itself.
 */
export const ActionsTabs: React.FC = () => {
  const { goto } = useNavigation();

  // Read the active tab from the matched route's handle, so the router remains
  // the single source of truth for which tab a URL maps to. Only one matched
  // route carries an `actionsTab` handle; fall back to the index tab
  // defensively.
  const matches = useMatches();
  const activeTab: ActionsTabName =
    matches
      .map((match) => actionsTabFromHandle(match.handle))
      .find((tab): tab is ActionsTabName => tab !== undefined) ?? "actions";

  const title =
    ACTIONS_TABS.find((tab) => tab.tab === activeTab)?.title ?? "Actions";

  return (
    <PageLayout title={`Roboto - ${title}`} headerContentLeft={<>{title}</>}>
      <div className={styles.page}>
        <div>
          <div className={styles.tabBar}>
            <RobotoStyledTabs
              value={activeTab}
              onChange={(_event, newTab: ActionsTabName) => {
                goto.actions({ tab: newTab });
              }}
              aria-label="actions tabs"
            >
              {ACTIONS_TABS.map((tab) => (
                <RobotoStyledTab
                  key={tab.tab}
                  value={tab.tab}
                  label={tab.label}
                />
              ))}
            </RobotoStyledTabs>
          </div>
          <hr className={styles.divider} />
        </div>
        <div className={styles.tabBody}>
          <Outlet />
        </div>
      </div>
    </PageLayout>
  );
};
